import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { DollarSign, Plus, Trash2, X, FileText, Check, Clock, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import toast from 'react-hot-toast';

const BUDGET_CATS = {
  materials: { label: 'Materiales', color: '#D4A017' },
  labor: { label: 'Mano de obra', color: '#3B82F6' },
  equipment_rental: { label: 'Alquiler equipos', color: '#8B5CF6' },
  transport: { label: 'Transporte', color: '#06B6D4' },
  permits: { label: 'Permisos', color: '#F59E0B' },
  engineering: { label: 'Ingeniería', color: '#10B981' },
  subcontract: { label: 'Subcontratas', color: '#EC4899' },
  overhead: { label: 'Gastos generales', color: '#64748B' },
  contingency: { label: 'Contingencia', color: '#EF4444' },
  other: { label: 'Otros', color: '#78716C' },
};

const PAY_STATUS = {
  pending: { label: 'Pendiente', color: '#F59E0B', icon: Clock },
  partial: { label: 'Parcial', color: '#3B82F6', icon: Clock },
  paid: { label: 'Pagado', color: '#10B981', icon: Check },
  overdue: { label: 'Vencido', color: '#EF4444', icon: AlertTriangle },
};

export default function Budget() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [lines, setLines] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [tab, setTab] = useState('budget');
  const [showCreateLine, setShowCreateLine] = useState(false);
  const [showCreateExpense, setShowCreateExpense] = useState(false);
  const [lineForm, setLineForm] = useState({ category: 'materials', name: '', estimatedAmount: '', quantity: '', unitPrice: '', unit: '' });
  const [expForm, setExpForm] = useState({ budgetLineId: '', type: 'expense', reference: '', supplier: '', description: '', amount: '', taxAmount: '', expenseDate: new Date().toISOString().split('T')[0], dueDate: '' });

  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (selectedProject) loadData(); }, [selectedProject]);

  const loadData = async () => {
    try {
      const [l, e, s] = await Promise.all([
        api.get('/budget/lines', { params: { project_id: selectedProject } }),
        api.get('/budget/expenses', { params: { project_id: selectedProject } }),
        api.get('/budget/summary', { params: { project_id: selectedProject } }),
      ]);
      setLines(l.data); setExpenses(e.data); setSummary(s.data);
    } catch (e) {}
  };

  const createLine = async () => {
    if (!lineForm.name) return toast.error('Nombre requerido');
    try {
      await api.post('/budget/lines', { projectId: selectedProject, ...lineForm, estimatedAmount: parseFloat(lineForm.estimatedAmount) || 0, quantity: parseFloat(lineForm.quantity) || null, unitPrice: parseFloat(lineForm.unitPrice) || null });
      toast.success('Partida creada'); setShowCreateLine(false); loadData();
      setLineForm({ category: 'materials', name: '', estimatedAmount: '', quantity: '', unitPrice: '', unit: '' });
    } catch (e) { toast.error('Error'); }
  };

  const createExpense = async () => {
    if (!expForm.amount) return toast.error('Importe requerido');
    try {
      await api.post('/budget/expenses', { projectId: selectedProject, ...expForm, amount: parseFloat(expForm.amount), taxAmount: parseFloat(expForm.taxAmount) || 0 });
      toast.success('Gasto registrado'); setShowCreateExpense(false); loadData();
      setExpForm({ budgetLineId: '', type: 'expense', reference: '', supplier: '', description: '', amount: '', taxAmount: '', expenseDate: new Date().toISOString().split('T')[0], dueDate: '' });
    } catch (e) { toast.error('Error'); }
  };

  const markPaid = async (id) => {
    try {
      await api.put(`/budget/expenses/${id}`, { paymentStatus: 'paid', paymentDate: new Date().toISOString().split('T')[0] });
      toast.success('Marcado como pagado'); loadData();
    } catch (e) { toast.error('Error'); }
  };

  const deleteLine = async (id) => { if (window.confirm('¿Eliminar partida?')) { await api.delete(`/budget/lines/${id}`); loadData(); } };
  const deleteExpense = async (id) => { if (window.confirm('¿Eliminar gasto?')) { await api.delete(`/budget/expenses/${id}`); loadData(); } };

  const totalEstimated = parseFloat(summary.total_estimated) || 0;
  const totalSpent = parseFloat(summary.total_spent) || 0;
  const deviation = totalEstimated > 0 ? ((totalSpent - totalEstimated) / totalEstimated * 100).toFixed(1) : 0;
  const chartData = (summary.byCategory || []).map(c => ({
    name: BUDGET_CATS[c.category]?.label || c.category,
    estimado: parseFloat(c.estimated), real: parseFloat(c.actual),
    color: BUDGET_CATS[c.category]?.color || '#64748B',
  }));

  return (
    <div className="budget-page">
      <div className="page-header">
        <div><h1><DollarSign size={24} /> Presupuesto</h1><p className="subtitle">Control de costes y facturación</p></div>
        <div className="page-header-actions">
          {tab === 'budget' && <button className="btn btn-primary" onClick={() => setShowCreateLine(true)}><Plus size={16} /> Partida</button>}
          {tab === 'expenses' && <button className="btn btn-primary" onClick={() => setShowCreateExpense(true)}><Plus size={16} /> Gasto</button>}
        </div>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Seleccionar proyecto...</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <div className="tab-buttons">
          <button className={`tab-btn ${tab === 'budget' ? 'active' : ''}`} onClick={() => setTab('budget')}>Presupuesto</button>
          <button className={`tab-btn ${tab === 'expenses' ? 'active' : ''}`} onClick={() => setTab('expenses')}>Gastos/Facturas</button>
        </div>
      </div>

      {selectedProject && (
        <>
          {/* KPIs */}
          <div className="bud-kpis">
            <div className="bud-kpi"><span className="bud-kpi-val">{totalEstimated.toLocaleString('es', { style: 'currency', currency: 'EUR' })}</span><span className="bud-kpi-lbl">Presupuesto</span></div>
            <div className="bud-kpi"><span className="bud-kpi-val">{totalSpent.toLocaleString('es', { style: 'currency', currency: 'EUR' })}</span><span className="bud-kpi-lbl">Gastado</span></div>
            <div className={`bud-kpi ${deviation > 0 ? 'warn' : 'ok'}`}><span className="bud-kpi-val">{deviation}%</span><span className="bud-kpi-lbl">Desviación</span></div>
            <div className="bud-kpi"><span className="bud-kpi-val">{Number(summary.pending_payment || 0).toLocaleString('es', { style: 'currency', currency: 'EUR' })}</span><span className="bud-kpi-lbl">Pte. de pago</span></div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bud-chart-card">
              <h3>Estimado vs Real por categoría</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" stroke="#64748B" fontSize={11} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={11} width={100} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }} formatter={v => `${Number(v).toLocaleString('es')} €`} />
                  <Bar dataKey="estimado" fill="#334155" name="Estimado" radius={[0,4,4,0]} />
                  <Bar dataKey="real" name="Real" radius={[0,4,4,0]}>
                    {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Budget Lines Tab */}
          {tab === 'budget' && (
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr><th>Categoría</th><th>Partida</th><th style={{ textAlign: 'right' }}>Estimado</th><th style={{ textAlign: 'right' }}>Real</th><th style={{ textAlign: 'right' }}>Diferencia</th><th></th></tr>
                </thead>
                <tbody>
                  {lines.map(l => {
                    const cat = BUDGET_CATS[l.category] || BUDGET_CATS.other;
                    const est = parseFloat(l.estimated_amount) || 0;
                    const act = parseFloat(l.actual_amount) || 0;
                    const diff = est - act;
                    return (
                      <tr key={l.id}>
                        <td><span className="cl-badge" style={{ background: cat.color + '20', color: cat.color }}>{cat.label}</span></td>
                        <td><span className="inv-item-name">{l.name}</span>{l.description && <span className="inv-brand">{l.description}</span>}</td>
                        <td className="inv-cost">{est.toLocaleString('es', { style: 'currency', currency: 'EUR' })}</td>
                        <td className="inv-cost">{act.toLocaleString('es', { style: 'currency', currency: 'EUR' })}</td>
                        <td className={`inv-cost ${diff < 0 ? 'low' : ''}`}>{diff.toLocaleString('es', { style: 'currency', currency: 'EUR' })}</td>
                        <td><button className="btn-icon" onClick={() => deleteLine(l.id)}><Trash2 size={14} style={{ color: '#EF4444' }} /></button></td>
                      </tr>
                    );
                  })}
                  {lines.length === 0 && <tr><td colSpan={6} className="inv-empty">Sin partidas presupuestarias</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* Expenses Tab */}
          {tab === 'expenses' && (
            <div className="inv-table-wrap">
              <table className="inv-table">
                <thead>
                  <tr><th>Fecha</th><th>Ref.</th><th>Proveedor</th><th>Partida</th><th style={{ textAlign: 'right' }}>Importe</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {expenses.map(e => {
                    const ps = PAY_STATUS[e.payment_status] || PAY_STATUS.pending;
                    return (
                      <tr key={e.id}>
                        <td>{new Date(e.expense_date).toLocaleDateString('es')}</td>
                        <td className="mono">{e.reference || '-'}</td>
                        <td>{e.supplier || '-'}</td>
                        <td>{e.budget_line_name || '-'}</td>
                        <td className="inv-cost">{Number(e.total_amount).toLocaleString('es', { style: 'currency', currency: 'EUR' })}</td>
                        <td><span className="cl-badge" style={{ background: ps.color + '20', color: ps.color }}>{ps.label}</span></td>
                        <td className="inv-actions">
                          {e.payment_status !== 'paid' && <button className="btn-icon" title="Marcar pagado" onClick={() => markPaid(e.id)}><Check size={14} style={{ color: '#10B981' }} /></button>}
                          <button className="btn-icon" onClick={() => deleteExpense(e.id)}><Trash2 size={14} style={{ color: '#EF4444' }} /></button>
                        </td>
                      </tr>
                    );
                  })}
                  {expenses.length === 0 && <tr><td colSpan={7} className="inv-empty">Sin gastos registrados</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Create Line Modal */}
      {showCreateLine && (
        <div className="modal-overlay" onClick={() => setShowCreateLine(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nueva Partida</h3><button className="btn-icon" onClick={() => setShowCreateLine(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Categoría</label><select value={lineForm.category} onChange={e => setLineForm(f => ({ ...f, category: e.target.value }))}>{Object.entries(BUDGET_CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
                <div className="form-group"><label>Importe estimado (€)</label><input type="number" step="0.01" value={lineForm.estimatedAmount} onChange={e => setLineForm(f => ({ ...f, estimatedAmount: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Nombre *</label><input value={lineForm.name} onChange={e => setLineForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Paneles solares JA Solar 550W" /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCreateLine(false)}>Cancelar</button><button className="btn btn-primary" onClick={createLine}>Crear</button></div>
          </div>
        </div>
      )}

      {/* Create Expense Modal */}
      {showCreateExpense && (
        <div className="modal-overlay" onClick={() => setShowCreateExpense(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nuevo Gasto</h3><button className="btn-icon" onClick={() => setShowCreateExpense(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Partida presupuestaria</label><select value={expForm.budgetLineId} onChange={e => setExpForm(f => ({ ...f, budgetLineId: e.target.value }))}><option value="">Sin asignar</option>{lines.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
                <div className="form-group"><label>Tipo</label><select value={expForm.type} onChange={e => setExpForm(f => ({ ...f, type: e.target.value }))}><option value="expense">Gasto</option><option value="invoice">Factura</option><option value="credit_note">Nota crédito</option></select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Nº Referencia/Factura</label><input value={expForm.reference} onChange={e => setExpForm(f => ({ ...f, reference: e.target.value }))} /></div>
                <div className="form-group"><label>Proveedor</label><input value={expForm.supplier} onChange={e => setExpForm(f => ({ ...f, supplier: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Descripción</label><input value={expForm.description} onChange={e => setExpForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label>Base imponible (€) *</label><input type="number" step="0.01" value={expForm.amount} onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))} /></div>
                <div className="form-group"><label>IVA (€)</label><input type="number" step="0.01" value={expForm.taxAmount} onChange={e => setExpForm(f => ({ ...f, taxAmount: e.target.value }))} /></div>
                <div className="form-group"><label>Total</label><input disabled value={((parseFloat(expForm.amount) || 0) + (parseFloat(expForm.taxAmount) || 0)).toFixed(2) + ' €'} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Fecha gasto</label><input type="date" value={expForm.expenseDate} onChange={e => setExpForm(f => ({ ...f, expenseDate: e.target.value }))} /></div>
                <div className="form-group"><label>Fecha vencimiento</label><input type="date" value={expForm.dueDate} onChange={e => setExpForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCreateExpense(false)}>Cancelar</button><button className="btn btn-primary" onClick={createExpense}>Registrar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
